import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useOportunidade } from '../../oportunidades/hooks';
import { useUsuarios } from '../../usuarios/hooks';
import { CommercialBaseSelect } from '../../base-comercial/components/CommercialBaseSelect';
import { useCriarPropostaDeOportunidade } from '../hooks';

/**
 * Wizard "Gerar Proposta" a partir de uma Oportunidade (US-COM-006,
 * task 15.3 — design.md §D3).
 *
 * **Cópia congelada.** O backend faz a herança na criação
 * (`ProposalCreationService.createFromOpportunity`); aqui pré-preenchemos
 * o formulário com os campos da Oportunidade para o usuário revisar. O
 * usuário pode sobrescrever responsável, escopo e observações antes de
 * criar. A Base Comercial é obrigatória (Oportunidade não carrega Base
 * — é a proposta que amarra a versão exata).
 */
export default function NovaPropostaDeOportunidadePage(): React.ReactElement {
  const navigate = useNavigate();
  const { oppId } = useParams<{ oppId: string }>();

  const { data: oportunidade, isLoading, isError, error } = useOportunidade(oppId);
  const { data: usuariosData } = useUsuarios({ limit: 200 });
  const criar = useCriarPropostaDeOportunidade();

  const [commercialBaseId, setCommercialBaseId] = useState('');
  const [dataValidade, setDataValidade] = useState(defaultValidade());
  const [responsavelId, setResponsavelId] = useState('');
  const [escopo, setEscopo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  // Pré-preenche quando a oportunidade chega.
  useEffect(() => {
    if (!oportunidade) return;
    setResponsavelId((prev) => prev || oportunidade.responsavelId);
    setEscopo((prev) => prev || oportunidade.escopo || '');
    setObservacoes((prev) => prev || oportunidade.observacoes || '');
  }, [oportunidade]);

  const usuarioOptions = (usuariosData?.data ?? []).map((u) => ({
    value: u.id,
    label: u.name || u.email,
    description: u.email,
  }));

  async function handleSubmit() {
    setErro(null);
    if (!oppId) return;
    if (!commercialBaseId) return setErro('Selecione a Base Comercial vigente.');
    if (!dataValidade) return setErro('Informe a data de validade.');
    try {
      const proposta = await criar.mutateAsync({
        opportunityId: oppId,
        payload: {
          commercialBaseId,
          dataValidade,
          responsavelComercialUserIdOverride: responsavelId || undefined,
          escopoOverride: escopo.trim() || undefined,
          observacoesOverride: observacoes.trim() || undefined,
        },
      });
      navigate(`/propostas/${proposta.id}`);
    } catch (e) {
      setErro(extractError(e));
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2 p-4 sm:p-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !oportunidade) {
    return (
      <div className="p-4 sm:p-6">
        <Alert variant="error">
          Não foi possível carregar a oportunidade: {(error as Error)?.message ?? 'erro'}
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>

      <h1 className="mb-2 text-xl font-semibold text-gray-900">
        Nova Proposta — Oportunidade {oportunidade.codigo}
      </h1>
      <p className="mb-6 text-sm text-gray-600">
        Cliente, responsável, escopo e observações vêm herdados da oportunidade — ajuste antes
        de criar. A herança é cópia congelada; alterações posteriores na oportunidade não
        retropropagam (RN-COM-003).
      </p>

      {erro && (
        <Alert variant="error" className="mb-4">
          {erro}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span className="text-xs font-medium uppercase text-gray-500">Cliente (herdado)</span>
          <div className="font-mono text-xs">{oportunidade.clientId}</div>
        </div>
        <AutocompleteSelect
          label="Responsável comercial (override)"
          options={usuarioOptions}
          value={responsavelId}
          onChange={setResponsavelId}
        />
        <CommercialBaseSelect
          clientId={oportunidade.clientId}
          value={commercialBaseId}
          onChange={setCommercialBaseId}
          allowNone={false}
        />
        <TextInput
          label="Data de validade *"
          type="date"
          value={dataValidade}
          onChange={(e) => setDataValidade(e.target.value)}
        />
        <div className="md:col-span-2">
          <Textarea
            label="Escopo (override opcional)"
            value={escopo}
            onChange={(e) => setEscopo(e.target.value)}
            rows={3}
            maxLength={4000}
          />
        </div>
        <div className="md:col-span-2">
          <Textarea
            label="Observações (override opcional)"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={2}
            maxLength={4000}
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSubmit} disabled={criar.isPending}>
          {criar.isPending ? 'Criando…' : 'Criar proposta'}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function defaultValidade(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string | string[] } } }).response;
    const msg = resp?.data?.message;
    if (Array.isArray(msg)) return msg.join('; ');
    if (typeof msg === 'string') return msg;
  }
  return e instanceof Error ? e.message : 'Erro desconhecido.';
}

export { NovaPropostaDeOportunidadePage };
